import fileExtension from "file-extension";

// Extensiones que el navegador decodifica y pinta por sí solo.
// No confundir con IMAGE_EXT de utils/index.js: ese agrupa por "archivo de
// diseño" e incluye psd, ai, tiff, dwg, 3ds. Nada de eso se renderiza.
export const EMBED_IMAGE_EXT = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "avif",
  "bmp",
  "ico"
];

export const EMBED_VIDEO_EXT = ["mp4", "webm", "ogv", "ogg", "mov", "m4v"];

// Las URLs se guardan limpias, pero el embed corre en el sitio de un cliente:
// ahí un fallo silencioso es caro, así que normalizamos igual.
export const extOf = url =>
  url ? fileExtension(String(url).split(/[?#]/)[0]).toLowerCase() : "";

export const isRenderableImage = url => EMBED_IMAGE_EXT.indexOf(extOf(url)) !== -1;

export const isPlayableVideo = url => EMBED_VIDEO_EXT.indexOf(extOf(url)) !== -1;

// mediaQualityURL solo difiere del original cuando la Lambda generó un derivado
// web: ObjectModel.js:383-384 los inicializa iguales al crear el objeto.
// Mismo criterio que handleSetSrc en utils/index.js.
export const hasWebDerivative = data =>
  !!data && !!data.mediaQualityURL && data.mediaQualityURL !== data.originalURL;

export const pickSource = (data, quality) => {
  if (!data) return "";
  if (quality === "low" && data.lowQualityURL) return data.lowQualityURL;
  if (quality === "media" && data.mediaQualityURL) return data.mediaQualityURL;
  return data.originalURL;
};

/**
 * Decide qué mostrar y con qué URL.
 * El tipo lo manda el backend (enum image|video|document|audio|font|other);
 * la extensión solo responde si el navegador puede con ESA url.
 * Devuelve { kind: "image" | "video" | "unsupported", src }.
 */
export const resolveMedia = (data, quality) => {
  if (!data) return { kind: "unsupported", src: "" };

  const requested = pickSource(data, quality);

  if (data.type === "image") {
    if (isRenderableImage(requested)) return { kind: "image", src: requested };
    // Un .psd o .tif pedido en calidad original: caer al derivado web.
    // Esto es lo que evita que el embed vuelva a fallar en formatos no-web.
    if (hasWebDerivative(data) && isRenderableImage(data.mediaQualityURL)) {
      return { kind: "image", src: data.mediaQualityURL };
    }
    return { kind: "unsupported", src: requested };
  }

  if (data.type === "video") {
    // El original puede ser .avi o ProRes, que ningún navegador reproduce.
    // La vista interna ya hace lo mismo (BunkeyHost.js:137-146).
    if (quality === "low" && isPlayableVideo(data.lowQualityURL)) {
      return { kind: "video", src: data.lowQualityURL };
    }
    if (isPlayableVideo(data.mediaQualityURL)) {
      return { kind: "video", src: data.mediaQualityURL };
    }
    if (isPlayableVideo(requested)) return { kind: "video", src: requested };
    return { kind: "unsupported", src: requested };
  }

  return { kind: "unsupported", src: requested };
};
